
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/use-toast';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Users, MessageCircle, Share2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import CollaboratorsList from '@/components/collaboration/CollaboratorsList';
import CommentsSection from '@/components/collaboration/CommentsSection';

interface CollaborativeDocument {
  id: string;
  title: string;
  content: string;
  lastEdited: string;
  createdBy: string;
  collaborators: Collaborator[];
  comments: Comment[];
}

interface Collaborator {
  id: string;
  name: string;
  email: string;
  role: 'editor' | 'viewer' | 'owner';
  avatar?: string;
  active: boolean;
}

interface Comment {
  id: string;
  text: string;
  author: string;
  timestamp: string;
  resolved: boolean;
}

const CollaborativeEditor = () => {
  const { docId } = useParams<{ docId: string }>();
  const [document, setDocument] = useState<CollaborativeDocument | null>(null);
  const [editedContent, setEditedContent] = useState<string>('');
  const [editedTitle, setEditedTitle] = useState<string>('');
  const [showCollaborators, setShowCollaborators] = useState<boolean>(false);
  const [showComments, setShowComments] = useState<boolean>(false);
  const { language, t } = useLanguage();

  useEffect(() => {
    // In a real app, this would fetch from an API
    // For now, we'll simulate by getting from localStorage or using sample data
    const getDocument = () => {
      const docs = JSON.parse(localStorage.getItem('adaaltBotCollabDocs') || '[]');
      const foundDoc = docs.find((doc: CollaborativeDocument) => doc.id === docId);
      
      if (foundDoc) {
        setDocument(foundDoc);
        setEditedContent(foundDoc.content);
        setEditedTitle(foundDoc.title);
      } else {
        // Sample data if not found
        const sampleDoc: CollaborativeDocument = {
          id: docId || '1',
          title: 'Collaborative Legal Document',
          content: 'This is a collaborative document that multiple team members can edit together.',
          lastEdited: new Date().toISOString(),
          createdBy: 'Current User',
          collaborators: [
            { id: '1', name: 'Current User', email: 'user@example.com', role: 'owner', active: true },
            { id: '2', name: 'Team Member 1', email: 'member1@example.com', role: 'editor', active: false },
            { id: '3', name: 'Team Member 2', email: 'member2@example.com', role: 'viewer', active: false }
          ],
          comments: [
            { id: '1', text: 'Please review section 3 carefully', author: 'Team Member 1', timestamp: '2024-05-08T14:30:00', resolved: false },
            { id: '2', text: 'Added the requested changes to the agreement terms', author: 'Current User', timestamp: '2024-05-08T15:45:00', resolved: true }
          ]
        };
        setDocument(sampleDoc);
        setEditedContent(sampleDoc.content);
        setEditedTitle(sampleDoc.title);
      }
    };
    
    getDocument();
  }, [docId]);

  const saveDocument = () => {
    if (!document) return;
    
    const updatedDoc: CollaborativeDocument = {
      ...document,
      title: editedTitle,
      content: editedContent,
      lastEdited: new Date().toISOString()
    };
    
    // In a real app, this would call an API
    // For now, we'll simulate by saving to localStorage
    const docs = JSON.parse(localStorage.getItem('adaaltBotCollabDocs') || '[]');
    const updatedDocs = docs.map((doc: CollaborativeDocument) => 
      doc.id === updatedDoc.id ? updatedDoc : doc
    );
    
    if (!updatedDocs.find((doc: CollaborativeDocument) => doc.id === updatedDoc.id)) {
      updatedDocs.push(updatedDoc);
    }
    
    localStorage.setItem('adaaltBotCollabDocs', JSON.stringify(updatedDocs));
    setDocument(updatedDoc);
    toast({
      title: "Success",
      description: "Document saved successfully"
    });
  };

  const addComment = (text: string) => {
    if (!document) return;
    
    const newComment: Comment = {
      id: Date.now().toString(),
      text,
      author: 'Current User',
      timestamp: new Date().toISOString(),
      resolved: false
    };
    
    const updatedDoc: CollaborativeDocument = {
      ...document,
      comments: [...document.comments, newComment]
    };
    
    setDocument(updatedDoc);
    
    // In a real app, this would call an API
    const docs = JSON.parse(localStorage.getItem('adaaltBotCollabDocs') || '[]');
    const updatedDocs = docs.map((doc: CollaborativeDocument) => 
      doc.id === updatedDoc.id ? updatedDoc : doc
    );
    
    if (!updatedDocs.find((doc: CollaborativeDocument) => doc.id === updatedDoc.id)) {
      updatedDocs.push(updatedDoc);
    }
    
    localStorage.setItem('adaaltBotCollabDocs', JSON.stringify(updatedDocs));
    toast({
      title: "Success", 
      description: "Comment added"
    });
  };

  const toggleCommentResolution = (commentId: string) => {
    if (!document) return;
    
    const updatedComments = document.comments.map(comment => 
      comment.id === commentId ? {...comment, resolved: !comment.resolved} : comment
    );
    
    const updatedDoc: CollaborativeDocument = {
      ...document,
      comments: updatedComments
    };
    
    setDocument(updatedDoc);
    
    // In a real app, this would call an API
    const docs = JSON.parse(localStorage.getItem('adaaltBotCollabDocs') || '[]');
    const updatedDocs = docs.map((doc: CollaborativeDocument) => 
      doc.id === updatedDoc.id ? updatedDoc : doc
    );
    
    localStorage.setItem('adaaltBotCollabDocs', JSON.stringify(updatedDocs));
  };

  const shareDocument = () => {
    // In a real app, this would generate a sharing link or show sharing options
    toast({
      title: "Success",
      description: "Sharing link copied to clipboard"
    });
  };

  if (!document) {
    return <div className="p-8 text-center">Loading document...</div>;
  }

  return (
    <div className="container mx-auto py-4 px-4">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Link to="/">
            <Button variant="ghost" size="sm" className="mr-2">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          </Link>
          <h1 className="text-2xl font-bold">Collaborative Editor</h1>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowCollaborators(!showCollaborators)}
            className="flex items-center"
          >
            <Users className="h-4 w-4 mr-1" />
            Collaborators ({document.collaborators.length})
          </Button>
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowComments(!showComments)}
            className="flex items-center"
          >
            <MessageCircle className="h-4 w-4 mr-1" />
            Comments ({document.comments.length})
          </Button>
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={shareDocument}
            className="flex items-center"
          >
            <Share2 className="h-4 w-4 mr-1" />
            Share
          </Button>
          
          <Button onClick={saveDocument}>
            Save Changes
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className={showCollaborators || showComments ? "md:col-span-2" : "md:col-span-3"}>
          <Card className="mb-4 p-4">
            <div className="mb-4">
              <label className="font-medium mb-1 block">Document Title</label>
              <Input 
                value={editedTitle} 
                onChange={(e) => setEditedTitle(e.target.value)}
                className="text-lg font-medium"
              />
            </div>
            
            <div className="mb-2 flex justify-between items-center">
              <div className="flex items-center">
                <Badge variant="outline" className="mr-2">
                  Collaborative
                </Badge>
                <span className="text-sm text-gray-500">
                  Last edited: {new Date(document.lastEdited).toLocaleString()}
                </span>
              </div>
              <div className="flex items-center">
                <span className="text-sm text-gray-500 mr-2">
                  Active users:
                </span>
                <div className="flex -space-x-2">
                  {document.collaborators
                    .filter(c => c.active)
                    .map(collaborator => (
                      <div 
                        key={collaborator.id}
                        className="h-6 w-6 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs"
                        title={collaborator.name}
                      >
                        {collaborator.name.charAt(0)}
                      </div>
                    ))}
                </div>
              </div>
            </div>
            
            <Textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="min-h-[500px] text-md"
              placeholder="Start editing the document..."
              dir={language === 'ur' ? 'rtl' : 'ltr'}
            />
          </Card>
        </div>
        
        {showCollaborators && (
          <div className="md:col-span-1">
            <CollaboratorsList 
              collaborators={document.collaborators}
              onClose={() => setShowCollaborators(false)}
            />
          </div>
        )}
        
        {showComments && (
          <div className="md:col-span-1">
            <CommentsSection 
              comments={document.comments}
              onAddComment={addComment}
              onToggleResolution={toggleCommentResolution}
              onClose={() => setShowComments(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CollaborativeEditor;
